import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { InputNumber as PrimeInputNumber, InputNumberProps } from 'primereact/inputnumber';

/** internals of the PrimeReact component we reuse so the value is parsed and clamped exactly as it is on blur */
type PrimeInputNumberInternals = PrimeInputNumber & {
  parseValue(text: string): number | string | null;
  validateValue(value: number | string | null): number | null;
  updateInputValue(value: number | null): void;
};

/**
 * Drop-in replacement for PrimeReact's InputNumber that also accepts values the user did not type:
 * browser autofill / autocomplete suggestions, text dropped into the field, etc.
 *
 * PrimeReact builds its value from keydown/keypress/paste only, and preventDefaults all of them, so a
 * natively fired `input` event on the inner input always comes from outside its own handling. It ignores
 * such an event, which left the field showing a value while the model stayed empty (RD-2887).
 */
export const InputNumber = forwardRef<PrimeInputNumber, InputNumberProps>((props, ref) => {
  const instanceRef = useRef<PrimeInputNumberInternals>(null);
  // the handler is attached once, so it has to read the current props instead of closing over them
  const propsRef = useRef(props);
  propsRef.current = props;

  const onExternalInput = useCallback((event: Event) => {
    const instance = instanceRef.current;
    const input = event.target as HTMLInputElement;
    const { disabled, readOnly, onChange, onValueChange, name, inputId } = propsRef.current;

    if (!instance || disabled || readOnly) return;
    // an IME composition is still in progress, PrimeReact picks it up on the keystrokes that follow
    if ((event as InputEvent).isComposing) return;

    const value = instance.validateValue(instance.parseValue(input.value));

    instance.updateInputValue(value);
    const originalEvent = event as unknown as React.SyntheticEvent;

    onChange?.({ originalEvent, value });
    onValueChange?.({
      originalEvent,
      value,
      stopPropagation: () => {},
      preventDefault: () => {},
      target: { name, id: inputId, value },
    });
  }, []);

  const detachRef = useRef<() => void>(null);

  const setInputRef = useCallback(
    (input: HTMLInputElement | null) => {
      detachRef.current?.();
      detachRef.current = null;

      if (input) {
        input.addEventListener('input', onExternalInput);
        detachRef.current = () => input.removeEventListener('input', onExternalInput);
      }

      const forwarded = propsRef.current.inputRef;
      if (typeof forwarded === 'function') forwarded(input);
      else if (forwarded) (forwarded as React.MutableRefObject<HTMLInputElement>).current = input;
    },
    [onExternalInput],
  );

  const setInstanceRef = useCallback(
    (instance: PrimeInputNumber | null) => {
      instanceRef.current = instance as PrimeInputNumberInternals;
      if (typeof ref === 'function') ref(instance);
      else if (ref) (ref as React.MutableRefObject<PrimeInputNumber>).current = instance;
    },
    [ref],
  );

  // PrimeReact hands the inner input over on mount only, so the listener is dropped on unmount instead
  useEffect(() => () => detachRef.current?.(), []);

  return <PrimeInputNumber {...props} ref={setInstanceRef} inputRef={setInputRef} />;
});

InputNumber.displayName = 'InputNumber';

export default InputNumber;
